import { Component, ViewChild, ElementRef, Input, Output, OnChanges, EventEmitter, SimpleChanges } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { AttachmentService } from 'src/app/_service/attachment.service';
import { TranslateService } from '@ngx-translate/core';
import { uploadedFile } from 'src/app/_model/claim';
import { MAX_ATTACHMENT_BYTES } from 'src/app/_model/string_literal';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.css',
  animations: [
    trigger('fadeInOut', [
      state('visible', style({ opacity: 1 })),
      state('hidden', style({ opacity: 0 })),
      transition('visible => hidden', [animate('500ms ease-out')]),
      transition('hidden => visible', [animate('500ms ease-in')])
    ])
  ],
})
export class FileUploadComponent implements OnChanges {
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  @Input() selectedVessel!: any;
  @Input() existingFiles!: any;
  @Output() fileListChange = new EventEmitter<any>();

  constructor(
    private attachmentService: AttachmentService,
    private translate: TranslateService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['existingFiles']) {
      const oldFiles = changes['existingFiles'].currentValue;
      oldFiles.forEach((f: any) => {
        const fileItem = f;
        fileItem.name = f.filename;
        fileItem.size = f.filesize;
        fileItem.status = 'done';
        fileItem.showUploaded = false;
        this.files.push(fileItem);
      })
    }
  }

  files: uploadedFile[] = [];

  updateFileList() {
    this.fileListChange.emit(this.files);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const uploadArea = event.currentTarget as HTMLElement;
    uploadArea.classList.add('dragover');
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    const uploadArea = event.currentTarget as HTMLElement;
    uploadArea.classList.remove('dragover');
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    const uploadArea = event.currentTarget as HTMLElement;
    uploadArea.classList.remove('dragover');

    const droppedFiles = event.dataTransfer?.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    this.addFiles(droppedFiles);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const target = event.target as HTMLInputElement;
    if (target.files)
      this.addFiles(target.files);
  }

  triggerFileInput() {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput.click();
  }

  addFiles(files: FileList) {
    const filesArray = Array.from(files);

    const totalSize = this.files.reduce((sum, file) => sum + file.size, 0) + filesArray.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_ATTACHMENT_BYTES) {
      window.alert(this.translate.instant("totalFileSizeErr"));
      return;
    }

    filesArray.forEach(file => {
      const existingFile = this.files.find(f => f.name === file.name);
      if (existingFile?.status === "done") {
        window.alert(
          this.translate.instant("fileAlreadyUploaded", {
            fileName: file.name
          })
        );
      } else {
        if (existingFile) 
          this.files = this.files.filter(f => f.name !== existingFile.name);
        const newFile = {
          name: file.name,
          size: file.size,
          status: "uploading",
          showUploaded: false,
          id: undefined,
          subscription: null as Subscription | null,
        }
        this.files.push(newFile);

        const formData = new FormData();
        formData.append("attach", file, file.name);
        if (this.selectedVessel)
          formData.append("companyId", this.selectedVessel?.manager_id);

        newFile.subscription = this.attachmentService.uploadFile(formData)
          .subscribe({
            next: (response: any) => {
              newFile.id = response.id,
              newFile.status = "done";
              newFile.showUploaded = true;

              setTimeout(() => {
                newFile.showUploaded = false;
              }, 1500);
            },
            error: (err) => {
              newFile.status = "error";
              window.alert(
                this.translate.instant("fileUploadFailed", {
                  fileName: file.name
                })
              );
            },
            complete: () => {
              this.updateFileList();
            }
          });
      }
    });

    this.fileInputRef.nativeElement.value = '';
  }

  formatFileSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    else if (bytes < 1024*1024) return `${(bytes / 1024).toFixed(1)} KB`;
    else return `${(bytes / (1024*1024)).toFixed(1)} MB`;
  }

  removeFile(file: any) {
    file?.subscription?.unsubscribe();
    this.files = this.files.filter(f => f.name !== file.name);
    this.updateFileList();
  }

}
